/**
 * RFI Attachments Store Slice
 *
 * GW-6344: Extracted from rfiSlice (which had no attachment state) into its own slice.
 * Manages attachment lists keyed by rfiId with loading/error state.
 *
 * Note: upload/download/flag/confirm are async operations that do NOT require long-lived
 * store state — callers get the result directly from the API and can optimistically update
 * the list. Only the fetched list is stored.
 */

import { rfiAttachmentsApi } from '@/api/rfiAttachments';
import type {
  PresignedDownloadResponse,
  PresignedUploadResponse,
  RFIAttachment,
} from '@/types/rfi-attachment.types';

import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface RFIAttachmentsSlice {
  /** Attachment lists keyed by rfiId */
  attachmentsByRFI: Record<string, RFIAttachment[]>;
  /** Global loading flag (covers fetchAttachments) */
  rfiAttachmentsLoading: boolean;
  /** Last error message, null when clean */
  rfiAttachmentsError: string | null;

  // Actions
  fetchAttachments: (rfiId: string, params?: { limit?: number; cursor?: string }) => Promise<void>;
  uploadAttachment: (
    rfiId: string,
    data: { fileName: string; fileSize: number; contentType: string }
  ) => Promise<PresignedUploadResponse>;
  confirmAttachment: (rfiId: string, attachmentId: string) => Promise<RFIAttachment>;
  deleteAttachment: (rfiId: string, attachmentId: string) => Promise<void>;
  flagAttachment: (rfiId: string, attachmentId: string) => Promise<RFIAttachment>;
  downloadAttachment: (rfiId: string, attachmentId: string) => Promise<PresignedDownloadResponse>;
  clearAttachmentsForRFI: (rfiId: string) => void;
  clearRFIAttachmentsError: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  attachmentsByRFI: {},
  rfiAttachmentsLoading: false,
  rfiAttachmentsError: null,
};

// ============================================================================
// Slice Creator
// ============================================================================

export const createRFIAttachmentsSlice: StateCreator<
  RFIAttachmentsSlice,
  [],
  [],
  RFIAttachmentsSlice
> = (set, _get) => ({
  ...initialState,

  fetchAttachments: async (rfiId, params) => {
    set({ rfiAttachmentsLoading: true, rfiAttachmentsError: null });
    try {
      const response = await rfiAttachmentsApi.listAttachments(rfiId, params);
      set((state) => ({
        attachmentsByRFI: {
          ...state.attachmentsByRFI,
          [rfiId]: response.attachments,
        },
        rfiAttachmentsLoading: false,
      }));
    } catch (err) {
      set({
        rfiAttachmentsLoading: false,
        rfiAttachmentsError: err instanceof Error ? err.message : 'Failed to fetch attachments',
      });
    }
  },

  uploadAttachment: async (rfiId, data) => {
    return rfiAttachmentsApi.uploadAttachment(rfiId, data);
  },

  confirmAttachment: async (rfiId, attachmentId) => {
    const result = await rfiAttachmentsApi.confirmAttachment(rfiId, attachmentId);
    // Update the attachment in-place
    set((state) => {
      const current = state.attachmentsByRFI[rfiId] ?? [];
      return {
        attachmentsByRFI: {
          ...state.attachmentsByRFI,
          [rfiId]: current.map((a) => (a.attachmentId === attachmentId ? result : a)),
        },
      };
    });
    return result;
  },

  deleteAttachment: async (rfiId, attachmentId) => {
    await rfiAttachmentsApi.deleteAttachment(rfiId, attachmentId);
    set((state) => {
      const current = state.attachmentsByRFI[rfiId] ?? [];
      return {
        attachmentsByRFI: {
          ...state.attachmentsByRFI,
          [rfiId]: current.filter((a) => a.attachmentId !== attachmentId),
        },
      };
    });
  },

  flagAttachment: async (rfiId, attachmentId) => {
    const result = await rfiAttachmentsApi.flagAttachment(rfiId, attachmentId);
    set((state) => {
      const current = state.attachmentsByRFI[rfiId] ?? [];
      return {
        attachmentsByRFI: {
          ...state.attachmentsByRFI,
          [rfiId]: current.map((a) => (a.attachmentId === attachmentId ? result : a)),
        },
      };
    });
    return result;
  },

  downloadAttachment: async (rfiId, attachmentId) => {
    return rfiAttachmentsApi.downloadAttachment(rfiId, attachmentId);
  },

  clearAttachmentsForRFI: (rfiId) => {
    set((state) => {
      const next = { ...state.attachmentsByRFI };
      delete next[rfiId];
      return { attachmentsByRFI: next };
    });
  },

  clearRFIAttachmentsError: () => {
    set({ rfiAttachmentsError: null });
  },
});
